import { create } from 'zustand';
import {
  onAuthStateChanged,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { callBootstrapUserProfile, callRecordReferral } from '@/lib/callables';
import { unregisterPushNotifications } from '@/lib/pushNotifications';
import type { UserDoc } from '@makhe/shared';

function isProfileComplete(data: Record<string, unknown> | null | undefined): boolean {
  if (!data) return false;
  return Boolean(data.roles && data.studentVerification && data.preferences && data.trust);
}

interface AuthState {
  initialised: boolean;
  firebaseUser: FirebaseUser | null;
  userDoc: UserDoc | null;
  setFirebaseUser: (u: FirebaseUser | null) => void;
  setUserDoc: (u: UserDoc | null) => void;
  isVerifiedStudent: () => boolean;
  isSeller: () => boolean;
  isAdmin: () => boolean;
}

export const useAuth = create<AuthState>((set, get) => ({
  initialised: false,
  firebaseUser: null,
  userDoc: null,
  setFirebaseUser: (u) => set({ firebaseUser: u, initialised: true }),
  setUserDoc: (u) => set({ userDoc: u }),
  isVerifiedStudent: () =>
    get().userDoc?.studentVerification?.status === 'verified',
  isSeller: () => Boolean(get().userDoc?.roles?.seller),
  isAdmin: () => Boolean(get().userDoc?.roles?.admin),
}));

let userDocUnsub: (() => void) | null = null;
let bootstrapRequested = false;

onAuthStateChanged(auth, (user) => {
  useAuth.getState().setFirebaseUser(user);

  if (userDocUnsub) {
    userDocUnsub();
    userDocUnsub = null;
  }

  if (user) {
    userDocUnsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      const data = snap.exists() ? snap.data() : null;
      if (data && !isProfileComplete(data) && !bootstrapRequested) {
        bootstrapRequested = true;
        void callBootstrapUserProfile()
          .catch(() => {
            bootstrapRequested = false;
          });
      }
      useAuth
        .getState()
        .setUserDoc(snap.exists() ? (snap.data() as UserDoc) : null);
    });
    const referrer = sessionStorage.getItem('makhe.referrer');
    if (referrer) {
      callRecordReferral({ referrerUid: referrer })
        .then(() => sessionStorage.removeItem('makhe.referrer'))
        .catch(() => null);
    }
  } else {
    bootstrapRequested = false;
    void unregisterPushNotifications();
    useAuth.getState().setUserDoc(null);
  }
});
